import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core'
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {Backend} from "@generated-backend-types";
import {ToastrService} from "ngx-toastr";
import {TranslateService} from "@ngx-translate/core";
import {BsModalRef, BsModalService} from "ngx-bootstrap/modal";
import {AsyncPipe, DecimalPipe, KeyValuePipe, NgForOf, NgIf} from '@angular/common';
import {PrivateUploaderService} from '../form-inputs/private-file-upload-field/services/private-uploader.service';
import {
  PrivateAttachmentsApiService
} from '../form-inputs/private-file-upload-field/services/private-attachments-api.service';
import {FileSizePipe} from '../../pipes/filesize.pipe';
import {ParseIsoPipeModule} from 'ngx-date-fns';
import {FormatInTimeZonePipe} from '../../pipes/format-in-time-zone.pipe';
import {Locale} from 'date-fns';
import {de} from 'date-fns/locale';
import {ContextMenuModule} from '@perfectmemory/ngx-contextmenu';
import {FileBrowserContextMenuActionEnum} from '../../enums/FileBrowserContextMenuActionEnum';
import {FormGroupComponent} from '../form-inputs/form-group/form-group.component';
import {AbstractFileManagerComponent} from '../abstract-file-manager.component';
import PrivateAttachment = Backend.App.Resources.Dashboard.Administration.PrivateAttachment;
import PrivateAttachmentAttachableTypeEnum = Backend.App.Enums.Administration.Attachments.PrivateAttachmentAttachableTypeEnum;

@Component({
  selector: 'app-private-file-browser[privateAttachments][privateAttachmentAttachableId][privateAttachmentAttachableType][privateAttachmentType][privateAttachmentMeta]',
  templateUrl: './private-file-browser.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [BsModalService],
  imports: [
    NgIf,
    NgForOf,
    FileSizePipe,
    ParseIsoPipeModule,
    FormatInTimeZonePipe,
    DecimalPipe,
    AsyncPipe,
    KeyValuePipe,
    ContextMenuModule,
    FormGroupComponent,
    ReactiveFormsModule,
  ],
  standalone: true
})
export class PrivateFileBrowserComponent extends AbstractFileManagerComponent {
  @Output() public reloadRequested = new EventEmitter<void>();

  @ViewChild('renameAttachmentModalTemplate') public renameAttachmentModalTemplate?: TemplateRef<BsModalRef>;
  public renameAttachmentForm = new FormGroup({name: new FormControl<string>('', [Validators.required])});
  public renameAttachmentModal?: BsModalRef<PrivateAttachment>;
  public readonly dfnsLocale: Locale = de;
  protected readonly FileBrowserContextMenuActionEnum = FileBrowserContextMenuActionEnum;
  private attachmentInRenameModal?: PrivateAttachment;

  public constructor(
    protected override cdr: ChangeDetectorRef,
    protected override privateUploadService: PrivateUploaderService,
    protected override toastr: ToastrService,
    protected override translate: TranslateService,
    private readonly bsModalService: BsModalService,
    private readonly privateAttachmentService: PrivateAttachmentsApiService,
  ) {
    super(cdr, privateUploadService, toastr, translate);

    this.privateUploadService.currentUploads.subscribe(() => this.cdr.markForCheck());
  }

  public uploadFile() {
    this.fileInput.nativeElement.click()
  }

  public reload() {
    this.reloadRequested.emit();
  }

  public executeAttachmentContextMenu(action: FileBrowserContextMenuActionEnum, event: {
    event: MouseEvent | KeyboardEvent;
    value?: PrivateAttachment;
  }) {
    switch (action) {
      case FileBrowserContextMenuActionEnum.DOWNLOAD:
        if (!event.value) return;
        this.openFile(event.value);
        break;
      case FileBrowserContextMenuActionEnum.RENAME:
        if (!event.value) return;
        this.showRenameAttachmentModal(event.value);
        break
      case FileBrowserContextMenuActionEnum.DELETE:
        if (!event.value) return;
        this.deleteAttachment(event.value);
        break;
      case FileBrowserContextMenuActionEnum.UPLOAD_FILE:
        this.uploadFile();
        break;
      case FileBrowserContextMenuActionEnum.RELOAD:
        this.reload();
        break;
    }
  }

  public showRenameAttachmentModal(attachment: PrivateAttachment) {
    this.attachmentInRenameModal = attachment;
    this.renameAttachmentForm.setValue({name: attachment.name})
    if (this.renameAttachmentModalTemplate) {
      this.renameAttachmentModal = this.bsModalService.show(this.renameAttachmentModalTemplate,
        {
          initialState: attachment,
          ignoreBackdropClick: true,
          keyboard: true,
          backdrop: true,
        }
      )
      this.renameAttachmentModal.onHide?.subscribe(
        () => {
          this.reload();
          this.renameAttachmentForm.reset()
        }
      )
    }
  }

  public renameAttachment() {
    if (!this.renameAttachmentForm.valid || !this.attachmentInRenameModal?.id) return;

    const data = {
      name: this.renameAttachmentForm.controls.name.value ?? '',
      attachable_type: PrivateAttachmentAttachableTypeEnum.PATIENTS,
    };

    this.privateAttachmentService.update(this.attachmentInRenameModal.id, data)
      .subscribe(() => {
        this.renameAttachmentModal?.hide();
        this.reload();
      });
  }

  public deleteAttachment(attachment: PrivateAttachment) {
    this.privateAttachmentService.delete(attachment.id)
      .subscribe(() => this.reload())
  }

  protected checkUploadsAllowedState(): void {
    const fileLimit = this.privateAttachmentMeta?.file_limits.find(limit => limit.attachment_type === this.privateAttachmentType);
    this.uploadsAllowed$.next(this.privateAttachments.length < (fileLimit?.max_files !== undefined ? fileLimit.max_files : 1))
  }
}
